/**
 * Ask-AI shapes consumed by the frontend. Mirror the backend Ask-AI module
 * schemas (`AskAiModel.citation` / `askPayload` / `message` / `sessionSummary` /
 * `sessionDetail`) field-for-field so the Eden-inferred wire type and these
 * domain types stay 1:1 (same convention as `lib/types/search.ts`).
 */

/** `ok` = the answer is authoritative; `warming` = the retrieval engine is still
 * spinning up, retry with backoff (no turn was persisted). */
export type TAskStatus = "ok" | "warming"

/** A grounding reference attached to an assistant answer. Mirrors the backend
 * `citation` schema exactly. `seekStart` (when present) deep-links to the exact
 * moment in the source meeting; `sourceChannel` drives a semantic tone accent. */
export interface IAskCitation {
  label: string
  meetingId: string
  meetingSubject: string
  meetingDate: string
  sourceChannel: string
  seekStart: number | null
  speaker: string | null
  /**
   * The excerpt the answer grounded on (the retrieval hit's text, trimmed).
   * Optional + nullable: the field is additive (Phase 1 backend), so citations
   * from before it existed — and old stored payloads — carry `undefined`/`null`.
   * Render defensively (absent → no quote block).
   */
  quote?: string | null
  /** End of the cited span in seconds, when known. Additive; absent on old data. */
  seekEnd?: number | null
}

/**
 * The ask response payload (the inner `data` of the success envelope). `ok`
 * carries the grounded answer + citations for the (echoed / newly-created)
 * session; `warming` carries only the status. A refusal is simply an `ok` answer
 * with no citations (the wire carries no separate refusal flag).
 */
export interface IAskPayload {
  status: TAskStatus
  /** The chat session the turn landed in — absent while `warming`. */
  sessionId?: string
  /** The grounded answer (or the canned "not in your meetings" refusal) — absent while `warming`. */
  answer?: string
  /** Only the excerpts the answer actually cited — absent while `warming`. */
  citations?: IAskCitation[]
  /**
   * Grounded follow-up questions the model suggested in the same call (2–3, or
   * none). Additive — absent from old payloads and while `warming`; render the
   * chips row only when present and non-empty.
   */
  followUps?: string[]
}

/**
 * The exact refusal string the backend emits when a question can't be answered
 * from the org's meetings (mirrors the backend `NO_ANSWER_REFUSAL` constant). It
 * is fixed and intentionally NOT localized, so a byte-exact match reliably flags
 * a refusal — the wire carries no separate refusal boolean. Used to give the
 * assistant bubble its distinct "no answer here" look.
 */
export const ASK_NO_ANSWER_REFUSAL = "I don't have information about that in your meetings."

/** True when an assistant answer is the canned "not in your meetings" refusal. */
export function isAskRefusal(content: string): boolean {
  return content.trim() === ASK_NO_ANSWER_REFUSAL
}

export type TAskRole = "user" | "assistant"

/** One persisted turn in a chat session. Mirrors the backend `message` schema. */
export interface IAskMessage {
  id: string
  role: TAskRole
  content: string
  citations: IAskCitation[] | null
  model: string | null
  createdAt: string
  /** Suggested follow-ups persisted with the assistant turn (additive; null/absent on old rows). */
  followUps?: string[] | null
}

/** A chat-session row for the sidebar list. Mirrors `sessionSummary`. */
export interface IAskSessionSummary {
  id: string
  title: string | null
  updatedAt: string
}

/** A chat session with its full message history. Mirrors `sessionDetail`. */
export interface IAskSessionDetail {
  id: string
  title: string | null
  messages: IAskMessage[]
}

/** Where an example question came from — a real open question the meetings raised,
 * or a static starter prompt. Mirrors the backend `examples` source enum. */
export type TAskExampleSource = "open_questions" | "starter"

/** A suggested starter question for the empty state. Mirrors `GET /ask-ai/examples`. */
export interface IAskExample {
  question: string
  source: TAskExampleSource
}

/**
 * The retrieval scope sent on the ask body (all fields optional). Mirrors the
 * Phase 1 backend `scope` schema; the engine already filters on these dimensions.
 * `all meetings` = omit scope entirely.
 */
export interface IAskScope {
  meetingId?: string
  seriesId?: string
  from?: string
  to?: string
}

/** The scope the composer chips express. UI-only; converted to {@link IAskScope}
 * for the wire by {@link askScopeToBody}. */
export interface IAskScopeSelection {
  kind: "all" | "month" | "series"
  /** Set only when `kind === "series"`. */
  seriesId?: string
}

/**
 * Convert a composer scope selection into the wire scope body. `all` → `undefined`
 * (no scope field sent, i.e. every meeting); `month` → `from` = start of the
 * current month; `series` → `seriesId`. `now` is injectable for tests.
 */
export function askScopeToBody(selection: IAskScopeSelection, now: Date = new Date()): IAskScope | undefined {
  if (selection.kind === "month") {
    return { from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString() }
  }
  if (selection.kind === "series" && selection.seriesId) {
    return { seriesId: selection.seriesId }
  }
  return undefined
}

// ---------------------------------------------------------------------------
// View-model types — shared by the Ask page, the ask-anywhere overlay and the
// ask-flow hook. They live here (not in a route-private folder) so a shared
// component can consume them without importing route internals. `IAskScopeSelection`
// above is likewise a UI-only shape kept alongside the wire types.
// ---------------------------------------------------------------------------

/**
 * The rendering phase of an assistant message. `complete` shows the answer;
 * `thinking` / `warming` render an in-flight placeholder bubble instead of
 * content (the ask-flow drives these while a question is in flight). A user
 * message is always `complete`.
 */
export type TChatMessagePhase = "complete" | "thinking" | "warming"

/** Which failure banner to show above the composer. `warming` is the soft
 * "engine still preparing" timeout, not a hard error. */
export type TAskErrorKind = "out-of-credits" | "unavailable" | "warming" | "generic" | "no-workspace"

/**
 * A message as a chat surface renders it — the smart wrapper flattens persisted
 * history plus the in-flight exchange into this shape so the view stays a pure
 * map. A `phase` other than `complete` renders the assistant placeholder.
 */
export interface IAskDisplayMessage {
  /** Stable React key (persisted id, or a synthetic id for the in-flight turn). */
  key: string
  role: TAskRole
  content: string
  citations?: IAskCitation[] | null
  refused?: boolean
  phase?: TChatMessagePhase
  /** Suggested follow-ups on an assistant answer (rendered as chips). */
  followUps?: string[] | null
}
